// In-memory access-token cache. The refresh token lives ONLY in an HTTP-only
// cookie set by auth-service — it must never touch JavaScript. Keeping the
// access token in memory (not localStorage) defeats XSS exfiltration; the
// cost is that a hard reload triggers a silent /auth/refresh round trip.

import type { JwtClaims } from "@/lib/auth/jwt";
import { decodeJwt } from "@/lib/auth/jwt";

type Listener = (token: string | null) => void;

class TokenStoreImpl {
  private token: string | null = null;
  private claims: JwtClaims | null = null;
  private listeners = new Set<Listener>();

  get accessToken(): string | null {
    return this.token;
  }

  get jwtClaims(): JwtClaims | null {
    return this.claims;
  }

  set(token: string | null): void {
    this.token = token;
    this.claims = token ? decodeJwt(token) : null;
    for (const listener of this.listeners) listener(token);
  }

  clear(): void {
    this.set(null);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  expiresInSeconds(): number | null {
    if (!this.claims?.exp) return null;
    return this.claims.exp - Math.floor(Date.now() / 1000);
  }
}

export const tokenStore = new TokenStoreImpl();
export type TokenStore = typeof tokenStore;
